import React, { useEffect, useRef } from "react";

/**
 * Встроенная камера (getUserMedia) без модалок и без внешних переключателей.
 * @param {"user"|"environment"} facing — фронт/тил
 * @param {(file: File) => void} onShot — callback после снимка
 * @param {(err?: any) => void} onCancel — callback при отмене/ошибке
 * @param {number} height — высота видоискателя (px)
 */
export default function CameraCapture({ facing = "environment", onShot, onCancel, height = 280 }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    let active = true;

    async function start() {
      try {
        const strict = { video: { facingMode: { exact: facing } }, audio: false };
        const soft = { video: { facingMode: { ideal: facing } }, audio: false };
        let stream;
        try {
          stream = await navigator.mediaDevices.getUserMedia(strict);
        } catch {
          stream = await navigator.mediaDevices.getUserMedia(soft);
        }
        if (!active) return;
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
      } catch (err) {
        console.warn("CameraCapture error:", err);
        onCancel?.(err);
      }
    }

    start();
    return () => {
      active = false;
      streamRef.current?.getTracks?.().forEach((t) => t.stop());
      streamRef.current = null;
    };
  }, [facing, onCancel]);

  const takeShot = async () => {
    const video = videoRef.current;
    if (!video) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth || 1280;
    canvas.height = video.videoHeight || 720;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.92));
    const file = new File([blob], `photo-${Date.now()}.jpg`, { type: "image/jpeg" });
    onShot?.(file);
  };

  return (
    <div style={{ position: "relative", width: "100%", borderRadius: 12, overflow: "hidden", background: "#000" }}>
      <video
        ref={videoRef}
        playsInline
        muted
        style={{ width: "100%", height, objectFit: "cover" }}
      />
      <div
        style={{
          position: "absolute",
          bottom: 12,
          left: 0,
          right: 0,
          display: "flex",
          justifyContent: "center",
          gap: 12,
          padding: "0 12px",
        }}
      >
        <button
          onClick={takeShot}
          style={{
            padding: "10px 18px",
            borderRadius: 28,
            background: "#1677ff",
            color: "#fff",
            border: "none",
            fontWeight: 600,
          }}
        >
          📸 Зняти
        </button>
        <button
          onClick={() => onCancel?.()}
          style={{
            padding: "10px 18px",
            borderRadius: 28,
            background: "#999",
            color: "#fff",
            border: "none",
            fontWeight: 600,
          }}
        >
          ✖ Закрити
        </button>
      </div>
    </div>
  );
}
